//
// ============================================================================
// PAGINATION ENVELOPE — defensive unwrap boundary
// ============================================================================
//
// `Paged<T>` (see `./pagination.types.ts`) is a LOCAL mirror of the backend
// `PagedResponse<T>`. The backend OpenAPI (`docs/core-api-specs/0.8.0.json`)
// documents the `page`/`pageSize` request params but leaves every list response
// as an undocumented `"200": OK` stub — so the envelope shape is not machine-
// checkable against the spec, and the local type was hand-transcribed from
// backend C# this repo does not contain.
//
// `unwrapPaged` is the single defensive boundary every paged response passes
// through (via `collectAllPages` and the server-mode page fetchers). It makes a
// backend contract drift fail LOUDLY and diagnosably at ingestion, instead of
// crashing later as `undefined is not iterable` deep inside a render, and it
// fills in missing/garbled `page` metadata so a partial envelope degrades
// gracefully rather than breaking pagination.
//

import type { Paged, PageMetadata } from "./pagination.types";

/**
 * Thrown when a value that should be a paged envelope is not one (e.g. the
 * backend returned a bare array, an error body, or `null`). Carries the
 * offending raw value so the failure is diagnosable from the console/logs.
 */
export class PagedEnvelopeError extends Error {
  /** The raw value that failed validation. */
  readonly raw: unknown;

  constructor(raw: unknown) {
    super(`Expected a paged envelope ({ items, page }) but received: ${describe(raw)}`);
    this.name = "PagedEnvelopeError";
    this.raw = raw;
  }
}

/** Short, log-safe description of an unexpected value for the error message. */
function describe(raw: unknown): string {
  if (raw === null) return "null";
  if (Array.isArray(raw)) return `array(length=${raw.length})`;
  return typeof raw;
}

/** A finite, non-negative integer, or `undefined` if the input is neither. */
function finiteOrUndefined(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

/**
 * Defensively unwrap a backend paged response into a fully-normalized
 * `Paged<T>`.
 *
 * - Throws {@link PagedEnvelopeError} when `raw` is not an object or its `items`
 *   is not an array — the one hard requirement, since every consumer iterates
 *   `items`.
 * - Derives any missing/garbled `page` metadata so a partial envelope still
 *   paginates correctly: `totalItems` falls back to `items.length`; `size` to
 *   `items.length || 1`; `totalPages` is **computed** from `totalItems`/`size`
 *   whenever it is not a *positive* finite number (so an explicit `0` alongside
 *   real items is corrected, not trusted); `number` defaults to `1`.
 *
 * The returned `page` fields are always finite, with `number >= 1`,
 * `size >= 1`, `totalItems >= 0`, and `totalPages >= 1`.
 *
 * @typeParam T - Collection item type.
 * @param raw - The parsed HTTP response body.
 * @returns A normalized `Paged<T>`.
 */
export function unwrapPaged<T>(raw: unknown): Paged<T> {
  if (typeof raw !== "object" || raw === null || !Array.isArray((raw as Paged<T>).items)) {
    throw new PagedEnvelopeError(raw);
  }

  const env = raw as Paged<T>;
  const meta = (env.page ?? {}) as Partial<PageMetadata>;

  const totalItems = Math.max(0, finiteOrUndefined(meta.totalItems) ?? env.items.length);
  const size = Math.max(1, finiteOrUndefined(meta.size) ?? (env.items.length || 1));
  const number = Math.max(1, finiteOrUndefined(meta.number) ?? 1);

  // `totalPages` is only trusted when it is a positive finite number. A `0`
  // (or negative/NaN/missing) is derived from `totalItems`/`size` — this both
  // handles the empty set (→ 1 empty page) and a misreporting backend that
  // sends `totalPages: 0` alongside real items (→ the true page count).
  const reportedTotalPages = finiteOrUndefined(meta.totalPages);
  const totalPages =
    reportedTotalPages && reportedTotalPages > 0 ? reportedTotalPages : Math.max(1, Math.ceil(totalItems / size));

  return { items: env.items, page: { number, size, totalItems, totalPages } };
}
